"use client";

import { Button, Highlight, HStack, Text, VStack } from "@chakra-ui/react";
import type { Criterion } from "@/generated/fastapi";
import WithControlButtons from "@/shared/ui/actions/WithControlButtons";
import WithSwipeSelector from "@/shared/ui/gestures/WithSwipeSelector";

const DEFAULT_SUGGESTIONS_CAP = 5;

interface SearchCriteriaListProps {
  criteria: Criterion[];
  isMobileDevice: boolean;
  predictiveFiltering: boolean;
  searchQuery: string;
  highlightColor: string;
  showAll: boolean;
  onToggleShowAll: () => void;
  isCriterionSelected: (criterionId: string) => boolean;
  onToggleCriterion: (criterion: Criterion) => void;
  onDeleteCriterion: (criterion: Criterion) => Promise<void>;
}

export default function SearchCriteriaList({
  criteria,
  isMobileDevice,
  predictiveFiltering,
  searchQuery,
  highlightColor,
  showAll,
  onToggleShowAll,
  isCriterionSelected,
  onToggleCriterion,
  onDeleteCriterion,
}: SearchCriteriaListProps) {
  const visibleCriteria = showAll
    ? criteria
    : criteria.slice(0, DEFAULT_SUGGESTIONS_CAP);

  if (criteria.length === 0) {
    return null;
  }

  return (
    <>
      <Text>Criteria to build search query</Text>

      <VStack align="stretch">
        {visibleCriteria.map((criterion) => (
          <WithSwipeSelector
            key={criterion.id}
            isMobile={isMobileDevice}
            selection={{
              isSelected: isCriterionSelected(criterion.id),
              onToggle: () => onToggleCriterion(criterion),
              humanReadable: "Select criterion for batch operations",
            }}
          >
            <WithControlButtons
              key={criterion.id}
              delete={{
                onClick: () => onDeleteCriterion(criterion),
                mode: "permanent",
                humanReadable: "Delete criterion permanently",
              }}
              clipboard={{
                value: criterion.mindstring,
                humanReadable: "Copy criterion to clipboard",
              }}
            >
              <Highlight
                ignoreCase
                query={predictiveFiltering ? searchQuery : ""}
                styles={{
                  bg: highlightColor,
                  fontWeight: "medium",
                }}
              >
                {criterion.mindstring}
              </Highlight>
            </WithControlButtons>
          </WithSwipeSelector>
        ))}
      </VStack>

      {criteria.length > DEFAULT_SUGGESTIONS_CAP && (
        <HStack mt={2} justify="flex-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onToggleShowAll}
            aria-expanded={showAll}
          >
            {showAll
              ? `Show first ${DEFAULT_SUGGESTIONS_CAP} suggestions`
              : `Show all ${criteria.length - DEFAULT_SUGGESTIONS_CAP} suggestions`}
          </Button>
        </HStack>
      )}
    </>
  );
}
